import React, { useCallback, useEffect, useState } from "react";
import {
    Shield, Loader2, Flag, AlertTriangle, UserCheck, Ban, Check, RefreshCw,
    ShieldCheck, Clock, Play,
} from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const REASON_LABEL = {
    harassment: "Harassment / threats",
    scam_fraud: "Scam / fraud",
    discrimination: "Discrimination",
    spam: "Bulk / spam",
    other: "Other",
};

// Map the backend regex patterns to human-readable labels for admin reviewers.
// Keep these in sync with SCAM_KEYWORD_PATTERNS in routers/messaging.py.
const PATTERN_LABELS = {
    "\\b\\d{3}-\\d{2}-\\d{4}\\b": "SSN format",
    "\\bbank account\\b": "bank account",
    "\\brouting number\\b": "routing number",
    "\\bgift ?card(s)?\\b": "gift card(s)",
    "\\bwire transfer\\b": "wire transfer",
    "\\bpay (a )?fee\\b": "pay fee",
    "\\bpay (for )?training\\b": "pay for training",
    "\\bzelle\\b": "Zelle",
    "\\bvenmo\\b": "Venmo",
    "\\bcash app\\b": "Cash App",
};
const prettyPattern = (p) => PATTERN_LABELS[p] || p.replace(/\\b|\\d\{\d,\d\}|\\|[?*+]/g, "").replace(/[()]/g, "").trim() || p;

const AdminModeration = () => {
    const [tab, setTab] = useState("reports"); // reports | scam | snoozed | resolved
    const [reports, setReports] = useState([]);
    const [flags, setFlags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(null); // report row being resolved
    const [action, setAction] = useState("dismiss");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    // iter-167 — Snooze dialog state (scam flag + chosen duration + reason).
    const [snoozing, setSnoozing] = useState(null);
    const [snoozeDuration, setSnoozeDuration] = useState("7d");
    const [snoozeReason, setSnoozeReason] = useState("");
    const [snoozingBusy, setSnoozingBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            if (tab === "scam") {
                const { data } = await api.get(`/admin/moderation/scam-flags?status=open`);
                setFlags(data.flags || []);
            } else if (tab === "snoozed") {
                const { data } = await api.get(`/admin/moderation/scam-flags?status=snoozed`);
                setFlags(data.flags || []);
            } else {
                const status = tab === "resolved" ? "warned" : "open";
                const { data } = await api.get(`/admin/moderation/reports?status=${status}`);
                setReports(data.reports || []);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    const openAction = (report, preset = "dismiss") => {
        setActing(report);
        setAction(preset);
        setNotes("");
    };
    const submitAction = async () => {
        if (!acting) return;
        setSubmitting(true);
        try {
            const { data } = await api.post(
                `/admin/moderation/reports/${acting.id}/action`,
                { action, notes },
            );
            toast.success(`Report ${data.status}.`);
            setActing(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSubmitting(false); }
    };
    const reinstate = async (user_id) => {
        if (!window.confirm("Reinstate this user's messaging access?")) return;
        try {
            await api.post(`/admin/moderation/users/${user_id}/reinstate`);
            toast.success("User reinstated. Open reports against them have been cleared.");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // iter-167 — Snooze / unsnooze scam flags.
    const openSnooze = (flag) => {
        setSnoozing(flag);
        setSnoozeDuration("7d");
        setSnoozeReason("");
    };
    const submitSnooze = async () => {
        if (!snoozing) return;
        setSnoozingBusy(true);
        try {
            await api.post(
                `/admin/moderation/scam-flags/${snoozing.id}/snooze`,
                { duration: snoozeDuration, reason: snoozeReason },
            );
            toast.success(`Snoozed for ${snoozeDuration}.`);
            setSnoozing(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSnoozingBusy(false); }
    };
    const unsnooze = async (flag) => {
        if (!window.confirm("Wake this snoozed flag back into the open queue?")) return;
        try {
            await api.post(`/admin/moderation/scam-flags/${flag.id}/unsnooze`);
            toast.success("Flag re-opened.");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div data-testid="admin-moderation-page">
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                        <Shield size={18} /> <p className="overline">Trust &amp; safety</p>
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-foreground mt-1">
                        Messaging moderation
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
                        Reports and keyword-flagged messages from the in-app messaging system.
                        Users auto-pause once they have 2+ unresolved reports against them.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="mod-refresh"
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                >
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1 mb-5" role="tablist">
                {[
                    { k: "reports", label: "User reports", icon: Flag },
                    { k: "scam", label: "Scam keyword flags", icon: AlertTriangle },
                    { k: "snoozed", label: "Snoozed", icon: Clock },
                    { k: "resolved", label: "Resolved", icon: ShieldCheck },
                ].map(({ k, label, icon: Icon }) => (
                    <button
                        key={k}
                        role="tab"
                        aria-selected={tab === k}
                        onClick={() => setTab(k)}
                        data-testid={`mod-tab-${k}`}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                            tab === k
                                ? "bg-emerald-600 text-white"
                                : "text-foreground hover:bg-muted/40"
                        }`}
                    >
                        <Icon size={12} /> {label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={20} className="animate-spin text-emerald-500" /></div>
            ) : tab === "scam" ? (
                <ScamFlagsTable flags={flags} snoozed={false} onSnooze={openSnooze} />
            ) : tab === "snoozed" ? (
                <ScamFlagsTable flags={flags} snoozed={true} onUnsnooze={unsnooze} />
            ) : (
                <ReportsTable rows={reports} tab={tab} onAct={openAction} onReinstate={reinstate} />
            )}

            {/* Action dialog */}
            <Dialog open={!!acting} onOpenChange={(o) => !o && setActing(null)}>
                <DialogContent data-testid="mod-action-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShieldCheck size={16} className="text-emerald-500" />
                            Resolve report
                        </DialogTitle>
                        <DialogDescription>
                            Choose how to handle the reported user. &quot;Suspend&quot; blocks their messaging
                            access until a platform admin reinstates them.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Action</label>
                            <Select value={action} onValueChange={setAction}>
                                <SelectTrigger data-testid="mod-action-select" className="h-9 mt-1 text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="dismiss">Dismiss — no violation</SelectItem>
                                    <SelectItem value="warn">Warn — mark as warned</SelectItem>
                                    <SelectItem value="suspend">Suspend messaging</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Notes (internal)</label>
                            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="mod-action-notes" />
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setActing(null)} className="btn-outline text-sm px-4 py-2" data-testid="mod-action-cancel">Cancel</button>
                        <button
                            onClick={submitAction}
                            disabled={submitting}
                            className="btn-green text-sm px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60"
                            data-testid="mod-action-submit"
                        >
                            {submitting ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                            Submit
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Snooze dialog */}
            <Dialog open={!!snoozing} onOpenChange={(o) => !o && setSnoozing(null)}>
                <DialogContent data-testid="mod-snooze-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Clock size={16} className="text-amber-500" />
                            Snooze scam flag
                        </DialogTitle>
                        <DialogDescription>
                            Hide this flag from the open queue. It will come back automatically
                            when the snooze window ends.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Duration</label>
                            <Select value={snoozeDuration} onValueChange={setSnoozeDuration}>
                                <SelectTrigger data-testid="mod-snooze-duration" className="h-9 mt-1 text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="24h">24 hours</SelectItem>
                                    <SelectItem value="7d">7 days</SelectItem>
                                    <SelectItem value="30d">30 days</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Why is this low-risk? (optional)</label>
                            <Textarea
                                rows={3} value={snoozeReason} onChange={(e) => setSnoozeReason(e.target.value)}
                                data-testid="mod-snooze-reason"
                                placeholder="e.g. candidate referenced Venmo as a past payment platform"
                                maxLength={500}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setSnoozing(null)} className="btn-outline text-sm px-4 py-2" data-testid="mod-snooze-cancel">
                            Cancel
                        </button>
                        <button
                            onClick={submitSnooze}
                            disabled={snoozingBusy}
                            className="btn-green text-sm px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60"
                            data-testid="mod-snooze-submit"
                        >
                            {snoozingBusy ? <Loader2 size={13} className="animate-spin" /> : <Clock size={13} />}
                            Snooze
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const ReportsTable = ({ rows, tab, onAct, onReinstate }) => {
    if (!rows.length) {
        return (
            <div className="card-soft p-10 text-center" data-testid="mod-empty">
                <ShieldCheck size={28} className="mx-auto text-emerald-500 mb-3" />
                <p className="text-sm text-foreground font-medium">
                    {tab === "resolved" ? "No resolved reports yet." : "Nothing to review 🎉"}
                </p>
            </div>
        );
    }
    return (
        <div className="space-y-3" data-testid="mod-reports-table">
            {rows.map((r) => (
                <div key={r.id} data-testid={`mod-report-${r.id}`} className="card-soft p-4">
                    <div className="flex items-start gap-3 flex-wrap">
                        <div className="flex-1 min-w-[260px]">
                            <div className="text-xs text-muted-foreground">
                                <span className="font-semibold text-foreground">{REASON_LABEL[r.reason] || r.reason}</span>
                                {" · "}{new Date(r.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                            </div>
                            <div className="mt-1 text-xs text-foreground">
                                Reporter: <code className="text-[11px]">{r.reporter_user_id}</code>
                            </div>
                            <div className="text-xs text-foreground">
                                Reported: <code className="text-[11px]">{r.reported_user_id}</code>
                            </div>
                            {r.details && (
                                <div className="mt-2 text-xs text-muted-foreground italic rounded bg-muted/40 border border-border p-2">
                                    “{r.details}”
                                </div>
                            )}
                            {Array.isArray(r.recent_messages) && r.recent_messages.length > 0 && (
                                <div className="mt-3">
                                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Last messages</div>
                                    <div className="space-y-1">
                                        {r.recent_messages.map((m, i) => (
                                            <div key={i} className="text-[11px] text-foreground">
                                                <span className="opacity-60 mr-1">[{m.sender_user_id === "system" ? "system" : m.sender_user_id.slice(0, 8)}]</span>
                                                {m.body}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {tab === "resolved" ? (
                                <button
                                    onClick={() => onReinstate(r.reported_user_id)}
                                    data-testid={`mod-reinstate-${r.id}`}
                                    className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1"
                                >
                                    <UserCheck size={11} /> Reinstate
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={() => onAct(r, "dismiss")}
                                        data-testid={`mod-dismiss-${r.id}`}
                                        className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1"
                                    >
                                        <Check size={11} /> Dismiss
                                    </button>
                                    <button
                                        onClick={() => onAct(r, "warn")}
                                        data-testid={`mod-warn-${r.id}`}
                                        className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-800 dark:text-amber-300 hover:bg-amber-500/15"
                                    >
                                        <AlertTriangle size={11} /> Warn
                                    </button>
                                    <button
                                        onClick={() => onAct(r, "suspend")}
                                        data-testid={`mod-suspend-${r.id}`}
                                        className="inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-semibold text-rose-700 dark:text-rose-400 hover:bg-rose-500/15"
                                    >
                                        <Ban size={11} /> Suspend
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

const ScamFlagsTable = ({ flags, snoozed = false, onSnooze, onUnsnooze }) => {
    if (!flags.length) {
        return (
            <div className="card-soft p-10 text-center" data-testid="mod-scam-empty">
                <ShieldCheck size={28} className="mx-auto text-emerald-500 mb-3" />
                <p className="text-sm text-foreground font-medium">
                    {snoozed ? "No snoozed flags." : "No scam-keyword flags open."}
                </p>
            </div>
        );
    }
    return (
        <div className="space-y-3" data-testid={snoozed ? "mod-snoozed-table" : "mod-scam-table"}>
            {flags.map((f) => (
                <div key={f.id} data-testid={`mod-flag-${f.id}`} className="card-soft p-4">
                    <div className="flex items-start gap-3 flex-wrap">
                        <div className="flex-1 min-w-[260px]">
                            <div className="text-xs text-muted-foreground">
                                <span className="font-semibold text-foreground">{(f.patterns || []).map(prettyPattern).join(", ")}</span>
                                {" · "}{new Date(f.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                            </div>
                            <div className="mt-1 text-xs text-foreground">
                                Sender: <code className="text-[11px]">{f.sender_user_id}</code>
                            </div>
                            {f.message?.body && (
                                <div className="mt-2 text-xs text-foreground rounded bg-muted/40 border border-border p-2 italic">
                                    “{f.message.body}”
                                </div>
                            )}
                            {snoozed && f.snoozed_until && (
                                <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
                                    <Clock size={11} /> Snoozed until {new Date(f.snoozed_until).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                                    {f.snoozed_reason && <span className="text-muted-foreground"> · “{f.snoozed_reason}”</span>}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {snoozed ? (
                                <button
                                    onClick={() => onUnsnooze?.(f)}
                                    data-testid={`mod-unsnooze-${f.id}`}
                                    className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1"
                                >
                                    <Play size={11} /> Wake up
                                </button>
                            ) : (
                                <button
                                    onClick={() => onSnooze?.(f)}
                                    data-testid={`mod-snooze-${f.id}`}
                                    className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-800 dark:text-amber-300 hover:bg-amber-500/15"
                                    title="Hide this flag for a while"
                                >
                                    <Clock size={11} /> Snooze
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default AdminModeration;
